import * as React from "react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ChevronDown, Loader } from "lucide-react";
import { GenerateReviewFormData } from "./GenerateReviewForm";
import { useDocument } from "../../hooks/useDocument";
import { useDefinitions } from "../../hooks/useDefinitions";
const TilesList = React.lazy(() => import("./TilesList"));
import { processDefinitions } from "../../services/review.service";
import { useGenerateReview } from "../../hooks/useGenerateReview";
import { GenerateReviewRequest } from "../../types/review.types";
import { useQueryClient } from "@tanstack/react-query";
import { useFuzzySearch } from "../../hooks/useFuzzySearch";

const GenerateReviewForm = React.lazy(() =>
  import("./GenerateReviewForm").then((module) => ({ default: module.GenerateReviewForm }))
);

interface ReviewProps {
  onReviewComplete?: () => void;
}

const Review: React.FC<ReviewProps> = ({ onReviewComplete }) => {
  const [selectedOptions, setSelectedOptions] = React.useState<Set<string>>(new Set());
  const [debouncedSelectedOptions, setDebouncedSelectedOptions] = React.useState<Set<string>>(
    new Set()
  );
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [abortControllers, setAbortControllers] = React.useState<AbortController[]>([]);
  const [elapsedTime, setElapsedTime] = React.useState(0);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);

  const { data: document } = useDocument();
  const { data: definitions } = useDefinitions();
  const generateReviewMutation = useGenerateReview();
  useQueryClient();
  const { buildIndex } = useFuzzySearch();

  React.useEffect(() => {
    if (isGenerating) {
      setElapsedTime(0);
      timerRef.current = setInterval(() => {
        setElapsedTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isGenerating]);

  // Debounce the selected options for review fetching
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSelectedOptions(selectedOptions);
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [selectedOptions]);

  const handleOptionChange = (keys: string[]) => {
    const newSelected = new Set(keys);
    setSelectedOptions(newSelected);
  };

  const handleGenerateReview = (data: GenerateReviewFormData) => {
    if (!document?._id || !document?.title) {
      return;
    }

    setIsGenerating(true);

    const requestsWithControllers = data.selectedOptions.map((queryTerm) => {
      const controller = new AbortController();
      const reviewRequest: GenerateReviewRequest = {
        document_id: [document._id!],
        document_filename: [document.title!],
        client: data.representingInput?.trim() || "",
        consider: data.considerationInput?.trim() || "",
        current_document_id: document._id!,
        query_term: queryTerm,
      };
      return { request: reviewRequest, controller };
    });

    setAbortControllers(requestsWithControllers.map((item) => item.controller));

    const mutations = requestsWithControllers.map(({ request, controller }) => {
      return generateReviewMutation.mutateAsync({
        request: request,
        signal: controller.signal,
      });
    });

    Promise.all(mutations.map((p) => p.catch((e) => e))).finally(() => {
      setIsGenerating(false);
      setAbortControllers([]);
      // Build index after generation finishes
      buildIndex();
      if (onReviewComplete) {
        onReviewComplete();
      }
    });
  };

  const handleCancelGenerating = () => {
    abortControllers.forEach((controller) => controller.abort());
    setAbortControllers([]);
    setIsGenerating(false);
  };

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (timeInSeconds % 60).toString().padStart(2, "0");
    return `(${minutes}:${seconds})`;
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex justify-end gap-2">
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <div
              role="button"
              className="w-full flex font-medium items-center rounded-md justify-between px-4 py-2 text-sm h-10 border"
            >
              Select review options
              <ChevronDown className="ml-2 size-3.5" />
            </div>
          </PopoverTrigger>
          <PopoverContent
            forceMount
            align="center"
            side="bottom"
            collisionPadding={20}
            className="overflow-y-auto p-4 max-h-[calc(100vh-200px)]"
            style={{ width: "var(--radix-popover-trigger-width)" }}
          >
            <React.Suspense
              fallback={
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <Loader className="animate-spin" size={48} />
                  <p>Loading...</p>
                </div>
              }
            >
              <GenerateReviewForm
                initialSelectedOptions={selectedOptions}
                onOptionSelected={handleOptionChange}
                onCancel={() => setIsPopoverOpen(false)}
                onGenerate={handleGenerateReview}
                isGenerating={isGenerating}
              />
            </React.Suspense>
          </PopoverContent>
        </Popover>
      </div>
      {isGenerating && (
        <Button variant="destructive" onClick={handleCancelGenerating} className="w-full">
          Cancel {formatTime(elapsedTime)}
        </Button>
      )}
      {document?._id && (
        <React.Suspense
          fallback={
            <div className="flex justify-center items-center h-[200px]">
              <Loader className="animate-spin" />
              <span className="ml-2">Loading...</span>
            </div>
          }
        >
          <TilesList
            selectedQueryTerms={Array.from(debouncedSelectedOptions)}
            documentId={document._id}
          />
        </React.Suspense>
      )}
    </div>
  );
};

export default Review;
